import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Button,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader
} from 'reactstrap';
import '../../../../custom.scss';
import { Section, SectionHeading } from '../../../common/Section';
import {
    accountUserSettingChangeEmail,
    accountUserSettingResendEmailConfirmation,
    accountUserSettingSetEmailChangeMessage
} from '../actions';
import {
    accountUserSettingsEmailChangeMessageSelector,
    accountUserSettingsEmailChangeStateSelector,
    accountUserSettingsProfileSettingsSelector
} from '../selectors';
import { isValidEmail } from '../../../../utils';

export type EmailChangeState = 'ok' | 'error' | undefined;

export const ChangeEmail: React.FC<{}> = () => {
    const profileSettings = useSelector(accountUserSettingsProfileSettingsSelector);

    const emailChangeMessage = useSelector(accountUserSettingsEmailChangeMessageSelector);
    const emailChangeState = useSelector(accountUserSettingsEmailChangeStateSelector);

    const dispatch = useDispatch();

    const [showDialog, setShowDialog] = React.useState(false);
    const [email, setEmail] = React.useState('');
    const [emailToConfirm, setEmailToConfirm] = React.useState('');

    React.useEffect(() => {
        setEmail(profileSettings.email ? profileSettings.email : '');
        setEmailToConfirm(profileSettings.emailToConfirm ? profileSettings.emailToConfirm : '');
    }, [profileSettings]);

    const onSetMessage = (msg: string, state: EmailChangeState) => {
        dispatch(accountUserSettingSetEmailChangeMessage({ msg, state }));
    };
    const onDismissMessage = () => {
        onSetMessage('', undefined);
    };

    return (
        <Section id="change-email">
            <SectionHeading>Email-Adresse ändern ({profileSettings.email})</SectionHeading>
            {(emailChangeState === 'ok' || emailChangeState === 'error') && (
                <Alert color={emailChangeState === 'error' ? 'danger' : 'success'} toggle={onDismissMessage}>
                    {emailChangeMessage}
                </Alert>
            )}
            <EmailConfirmationInfo />
            <Form>
                <FormGroup>
                    <Label>hinterlegte Email-Adresse</Label>
                    <Input readOnly={true} invalid={!profileSettings.emailValid} value={email} />
                    <FormFeedback>
                        Die Email-Adresse ist nicht gültig. Bitte teilen Sie uns eine korrekte Email-Adresse mit.
                    </FormFeedback>
                </FormGroup>
                {profileSettings.emailToConfirm && (
                    <FormGroup>
                        <Label>neue, unbestätigte Email-Adresse</Label>
                        <Input readOnly={true} value={emailToConfirm} />
                    </FormGroup>
                )}
            </Form>
            <Button onClick={() => setShowDialog(true)}>Email-Adresse ändern</Button>
            <ChangeEmailModalDialog isOpen={showDialog} close={() => setShowDialog(false)} />
        </Section>
    );
};

export const EmailConfirmationInfo: React.FC<{}> = () => {
    const profileSettings = useSelector(accountUserSettingsProfileSettingsSelector);

    const dispatch = useDispatch();

    if (profileSettings.emailToConfirm) {
        return (
            <Alert color="success">
                <h4>Unbestätigte Email-Adresse</h4>
                <p>
                    Sie haben die Email-Adresse auf '{profileSettings.emailToConfirm}' geändert ... aber diese noch
                    nicht über den Bestätigungslink aktiviert.
                </p>
                <hr />
                <Button color="warning" onClick={() => dispatch(accountUserSettingResendEmailConfirmation())}>
                    Bestätigungslink erneut versenden
                </Button>
            </Alert>
        );
    } else {
        return null;
    }
};

interface ModalProps {
    isOpen: boolean;
    close: () => void;
}

export const ChangeEmailModalDialog: React.FC<ModalProps> = props => {
    const [newEmail, setNewEmail] = React.useState('');
    const [newEmail2, setNewEmail2] = React.useState('');
    const [emailValid, setEmailValid] = React.useState(false);
    const [email2Equal, setEmail2Equal] = React.useState(true);

    const dispatch = useDispatch();

    const onUpdateEmail = (emailAddress: string) => {
        dispatch(accountUserSettingChangeEmail(emailAddress));
    };
    const onSetMessage = (msg: string, state: EmailChangeState) => {
        dispatch(accountUserSettingSetEmailChangeMessage({ msg, state }));
    };
    const onUpdate = () => {
        if (!newEmail || !isValidEmail(newEmail)) {
            onSetMessage(`Ungültige Email-Adresse: "${newEmail}"`, 'error');
        } else if (newEmail !== newEmail2) {
            onSetMessage('Die Email-Adressen sind nicht identisch.', 'error');
        } else if (onUpdateEmail) {
            onSetMessage('', undefined);
            onUpdateEmail(newEmail);
            setNewEmail('');
            setNewEmail2('');
            props.close();
        }
    };

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const email = e.currentTarget.value;
        if (e.currentTarget.name === 'newEmail') {
            setNewEmail(email);
            setEmailValid(isValidEmail(email));
            setEmail2Equal(email === newEmail2);
        } else {
            setNewEmail2(email);
            setEmail2Equal(email === newEmail);
        }
    };

    return (
        <Modal isOpen={props.isOpen} toggle={props.close}>
            <ModalHeader toggle={props.close}>Email-Adresse ändern</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <Label>neue Email-Adresse</Label>
                    <Input
                        type="email"
                        name="newEmail"
                        placeholder="neue Email-Adressse"
                        value={newEmail}
                        onChange={onChange}
                        valid={emailValid}
                        invalid={!emailValid && newEmail !== ''}
                    />
                    <FormFeedback>Ungültige Email-Adresse.</FormFeedback>
                </FormGroup>
                <FormGroup>
                    <Label>neue Email-Adresse wiederholen</Label>
                    <Input
                        type="email"
                        name="newEmail2"
                        placeholder="neue Email-Adressse wiederholen"
                        value={newEmail2}
                        onChange={onChange}
                        valid={email2Equal && newEmail !== ''}
                        invalid={!email2Equal && newEmail !== ''}
                    />
                    <FormFeedback>Die Email-Adressen sind nicht identisch.</FormFeedback>
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <Button onClick={props.close}>Abbrechen</Button>
                <Button color="success" onClick={onUpdate}>
                    Email-Adresse ändern
                </Button>
            </ModalFooter>
        </Modal>
    );
};
